/**
 * Endereços do Lead nas telas do CRM (ADR-0071) — a parte pura.
 *
 * O `EnderecosEditor` só devolve a lista dos cartões. A regra do cartão em
 * branco, do que vai em `enderecos` no POST/PATCH e de em qual cartão cada
 * erro aparece é a mesma do cliente e mora em `utils/endereco`; aqui fica o
 * que é só do lead (quando a lista conta como alteração no cockpit, o
 * endereço principal para o resumo).
 *
 * Lead não tem endereço de cobrança: nada aqui envia `cobranca`.
 */
import {
  enderecosParaPayload,
  errosDaRespostaNosCartoes,
  errosNosCartoes,
  normalizarCep,
  normalizarUf,
  prepararEnderecos,
  validarCartoes,
} from "../../utils/endereco";

export { errosNosCartoes, prepararEnderecos };

/** Endereços do detalhe do lead (`GET /api/crm/leads/{id}`); `[]` sem nenhum. */
export function enderecosDoLead(lead) {
  return Array.isArray(lead?.enderecos) ? lead.enderecos : [];
}

/** O endereço principal (o primeiro da lista) ou `null`. */
export function enderecoPrincipal(lead) {
  return enderecosDoLead(lead)[0] || null;
}

/** "São Paulo/SP" — só o que existe; "" sem endereço. */
export function cidadeUf(endereco) {
  return [endereco?.cidade, endereco?.estado]
    .map((parte) => String(parte ?? "").trim())
    .filter(Boolean)
    .join("/");
}

function textoAparado(valor) {
  return String(valor ?? "").replace(/\s+/g, " ").trim();
}

// O backend grava o CEP em 8 dígitos, a UF em sigla e o texto aparado:
// "01310-100" digitado sobre "01310100" gravado não é alteração.
function formaGravada(item) {
  return [
    item.id || "",
    textoAparado(item.titulo),
    normalizarCep(item.cep) ?? textoAparado(item.cep),
    textoAparado(item.logradouro),
    textoAparado(item.numero),
    textoAparado(item.complemento),
    textoAparado(item.bairro),
    textoAparado(item.cidade),
    normalizarUf(item.estado) ?? textoAparado(item.estado),
  ].join("|");
}

/**
 * A lista do editor muda o que está gravado? Ordem conta (o 1º é o principal).
 * `originais` = `lead.enderecos` como veio do servidor.
 */
export function enderecosAlterados(lista, originais) {
  const atuais = prepararEnderecos(lista).enderecos.map(formaGravada);
  const gravados = enderecosParaPayload(Array.isArray(originais) ? originais : []).map(formaGravada);
  return atuais.length !== gravados.length || atuais.some((forma, i) => forma !== gravados[i]);
}

/**
 * Validação antes de enviar: `[]` quando pode enviar; senão erros por cartão
 * (o formato da prop `erros` do editor). `originais` evita revalidar endereço
 * gravado que voltou igual — um legado só com a UF não trava a edição.
 */
export function validarEnderecosDoLead(lista, originais = []) {
  return validarCartoes(lista, { originais });
}

/**
 * Erros de endereço de uma falha da API (`err.payload.enderecos` do 400),
 * já nos cartões de `lista` — a mesma lista que foi enviada. `null` quando a
 * falha não é de endereço.
 */
export function errosDeEnderecoDaResposta(err, lista) {
  return errosDaRespostaNosCartoes(err, lista);
}
